import type { Country } from '../../constants/country/country';
import type { Currency } from '../../constants/currency/currency';

export type ShippingDuration = {
  /** Minimum delivery days */
  minDay: number;
  /** Maximum delivery days */
  maxDay: number;
};

export type DeliveryDateRange = {
  /** Minimum delivery date in ISO format (YYYY-MM-DD) */
  minDate: string;
  /** Maximum delivery date in ISO format (YYYY-MM-DD) */
  maxDate: string;
};

/**
 * Dynamic SLA block returned by `/shippingPolicies` when there are enough
 * recent samples (Tier 1). Missing when the backend falls back to static
 * `shippingDuration` dates (Tier 2) — see SSD-3986 Phase 2.
 *
 * Either percentile may be absent when the backend can't compute it (e.g. low
 * sample count for that bucket). When either `p70Date` or `p90Date` is missing,
 * consumers must fall back to `deliveryDateRange`.
 */
export type DynamicSla = {
  /** P70 delivery day count (calendar days, includes globalShippingDelayDay buffer) */
  p70Day?: number;
  /** P70 delivery calendar date in ISO format (YYYY-MM-DD) */
  p70Date?: string;
  /** P90 delivery day count (calendar days, includes globalShippingDelayDay buffer) */
  p90Day?: number;
  /** P90 delivery calendar date in ISO format (YYYY-MM-DD) */
  p90Date?: string;
  /** Number of samples used to compute the estimate */
  sampleSize: number;
};

export type ShippingPolicyItem = {
  /** Country code */
  countryCode: Country;
  /** Currency code */
  currencyCode: Currency;
  /** Shipping fee amount */
  shippingFee: number;
  /** Free shipping threshold amount */
  freeShippingThreshold: number;
  /** Shipping duration (business days) */
  shippingDuration: ShippingDuration;
  /** Delivery date range (calendar dates), derived from business days for display */
  deliveryDateRange?: DeliveryDateRange;
  /** SLA date in YYYYMMDD format */
  slaDate?: string;
  /** Global shipping system message — rendered verbatim as the seasonal notice line */
  systemMessage?: string;
  /** Average delivery days based on recent delivery data (calendar days) */
  avgDeliveryDay?: number;
  /** Average delivery date in ISO format (YYYY-MM-DD) */
  avgDeliveryDate?: string;
  /** Dynamic SLA estimate — present only when enough samples are available (Tier 1) */
  dynamicSla?: DynamicSla;
};
